import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
	ArrowLeft,
	Save,
	AlertCircle,
	Loader2,
	FolderOpen,
	FileText,
	Award,
	CheckSquare,
	Users,
} from 'lucide-react'
import { Button, Alert } from '@components/common'
import { ROLES, hasPermission } from '@config/roles'
import projectService from '@services/projectService'
import checklistService from '@services/checklistService'
import certificateService from '@services/certificateService'
import userService from '@services/userService'

// Sub-components
import BasicDetailsForm from './components/BasicDetailsForm'
import CertificatesForm from './components/CertificatesForm'
import ChecklistsForm from './components/ChecklistsForm'
import RolesForm from './components/RolesForm'

import './EditProject.scss'

/**
 * Edit Project Page Component
 *
 * Allows editing all project information:
 * - Basic details (name, description, dates, priority, status, etc.)
 * - Compliance standards (add/remove certificates)
 * - Checklists (full CRUD - edit, add, delete, reorder)
 */
const EditProject = ({ userRole = ROLES.COMPANY_ADMIN }) => {
	const { id } = useParams()
	const navigate = useNavigate()

	// Loading states
	const [loading, setLoading] = useState(true)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState(null)
	const [successMessage, setSuccessMessage] = useState(null)

	// Data states
	const [project, setProject] = useState(null)
	const [checklists, setChecklists] = useState([])
	const [availableCertificates, setAvailableCertificates] = useState([])
	const [teamMembers, setTeamMembers] = useState([])

	// Form states
	const [basicDetails, setBasicDetails] = useState({})
	const [selectedCertificates, setSelectedCertificates] = useState([])
	const [selectedRoles, setSelectedRoles] = useState([])
	const [checklistItems, setChecklistItems] = useState([])
	const [hasChanges, setHasChanges] = useState(false)

	// Active section for navigation
	const [activeSection, setActiveSection] = useState('basic')

	// Permissions
	const canEdit =
		hasPermission(userRole, 'canManageProjects') || hasPermission(userRole, 'canManageAssignedProjects')

	// Fetch project data on mount
	useEffect(() => {
		if (id) {
			fetchProjectData()
		}
	}, [id])

	const fetchProjectData = async () => {
		setLoading(true)
		setError(null)

		try {
			// Fetch project details
			const projectResponse = await projectService.getById(id)
			if (!projectResponse.success && !projectResponse.data) {
				setError('Failed to load project details')
				return
			}

			const projectData = projectResponse.data || projectResponse
			setProject(projectData)

			// Initialize basic details form
			setBasicDetails({
				name: projectData.name || '',
				description: projectData.description || '',
				type: projectData.type || 'compliance',
				priority: projectData.priority || 'medium',
				status: projectData.status || 'planning',
				assigned_to: projectData.assigned_to || '',
				start_date: projectData.start_date || '',
				target_date: projectData.target_date || '',
				industry: projectData.industry || '',
				domain: projectData.domain || '',
			})

			// Parse and set selected certificates
			let certIds = projectData.certificates || []
			if (typeof certIds === 'string') {
				try {
					certIds = JSON.parse(certIds)
				} catch {
					certIds = []
				}
			}
			setSelectedCertificates(Array.isArray(certIds) ? certIds.map((id) => parseInt(id, 10)) : [])

			// Fetch available certificates
			try {
				const certsResponse = await certificateService.getActive()
				if (certsResponse.data) {
					setAvailableCertificates(certsResponse.data)
				} else if (Array.isArray(certsResponse)) {
					setAvailableCertificates(certsResponse)
				}
			} catch (err) {
				console.warn('Could not load certificates:', err)
				setAvailableCertificates([])
			}

			// Fetch associated roles
			try {
				const rolesResponse = await projectService.getAssociatedRoles(id)
				if (rolesResponse.success || rolesResponse.data) {
					const roles = rolesResponse.data || rolesResponse || []
					const roleIds = roles.map((role) => parseInt(role.role_id, 10))
					setSelectedRoles(roleIds)
				}
			} catch (err) {
				console.warn('Could not load associated roles:', err)
				setSelectedRoles([])
			}

			// Fetch checklists
			try {
				const checklistResponse = await checklistService.getByProject(id)
				if (checklistResponse.success || checklistResponse.data) {
					const items = checklistResponse.data || checklistResponse || []
					setChecklists(items)
					setChecklistItems(items)
				}
			} catch (err) {
				console.warn('Could not load checklists:', err)
			}

			// Fetch team members
			try {
				if (projectData.company_id) {
					const usersResponse = await userService.getByCompany(projectData.company_id)
					if (usersResponse.success || usersResponse.data) {
						setTeamMembers(usersResponse.data || usersResponse || [])
					}
				}
			} catch (err) {
				console.warn('Could not load team members:', err)
			}
		} catch (err) {
			console.error('Error fetching project:', err)
			setError('Failed to load project. Please try again.')
		} finally {
			setLoading(false)
		}
	}

	// Handle back navigation
	const handleBack = () => {
		if (hasChanges) {
			if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
				navigate(`/projects/${id}`)
			}
		} else {
			navigate(`/projects/${id}`)
		}
	}

	// Handle basic details change
	const handleBasicDetailsChange = (field, value) => {
		setBasicDetails((prev) => ({ ...prev, [field]: value }))
		setHasChanges(true)
	}

	// Handle certificates change
	const handleCertificatesChange = (certificates) => {
		setSelectedCertificates(certificates)
		setHasChanges(true)
	}

	// Handle checklists change
	const handleChecklistsChange = (items) => {
		setChecklistItems(items)
		setHasChanges(true)
	}

	// Save all changes
	const handleSave = async () => {
		setSaving(true)
		setError(null)
		setSuccessMessage(null)

		try {
			// 1. Update project basic details and certificates
			const projectUpdateData = {
				...basicDetails,
				certificates: JSON.stringify(selectedCertificates),
				// Convert empty strings to null for foreign key fields
				assigned_to: basicDetails.assigned_to || null,
			}

			const projectResponse = await projectService.update(id, projectUpdateData)
			if (!projectResponse.success && projectResponse.error) {
				throw new Error(projectResponse.error || 'Failed to update project')
			}

			// 2. Handle checklist updates
			const originalIds = new Set(checklists.map((c) => c.id))
			const currentIds = new Set(checklistItems.filter((c) => c.id).map((c) => c.id))

			// Find deleted items (in original but not in current)
			const deletedItems = checklists.filter((c) => !currentIds.has(c.id))

			// Find new items (no id)
			const newItems = checklistItems.filter((c) => !c.id)

			// Find updated items (have id and in current)
			const updatedItems = checklistItems.filter((c) => c.id && originalIds.has(c.id))

			// Delete removed items
			for (const item of deletedItems) {
				await checklistService.delete(item.id)
			}

			// Create new items
			for (const item of newItems) {
				// Clean the item data - convert empty strings to null for foreign key fields
				const cleanedItem = {
					...item,
					project_id: id,
					assigned_to: item.assigned_to || null,
					certificate_id: item.certificate_id || null,
					workflow_id: item.workflow_id || null,
				}
				// Remove the temporary ID before sending to API
				delete cleanedItem._tempId
				await checklistService.create(cleanedItem)
			}

			// Update existing items
			for (const item of updatedItems) {
				const original = checklists.find((c) => c.id === item.id)
				// Check if there are any changes
				if (JSON.stringify(original) !== JSON.stringify(item)) {
					// Clean the item data - convert empty strings to null for foreign key fields
					const cleanedItem = {
						...item,
						assigned_to: item.assigned_to || null,
						certificate_id: item.certificate_id || null,
						workflow_id: item.workflow_id || null,
					}
					await checklistService.update(item.id, cleanedItem)
				}
			}

			// 3. Sync associated roles
			try {
				await projectService.syncRoles(id, selectedRoles)
			} catch (err) {
				console.warn('Failed to sync roles:', err)
				// Don't fail the entire save if roles fail
			}

			// Refresh data
			await fetchProjectData()
			setHasChanges(false)
			setSuccessMessage('Project updated successfully!')
		} catch (err) {
			console.error('Error saving project:', err)
			setError(err.message || 'Failed to save changes. Please try again.')
		} finally {
			setSaving(false)
		}
	}

	// Section navigation
	const sections = [
		{ id: 'basic', label: 'Basic Details', icon: FileText },
		{ id: 'certificates', label: 'Compliance Standards', icon: Award },
		{ id: 'roles', label: 'Associated Roles', icon: Users },
		{ id: 'checklists', label: 'Checklists', icon: CheckSquare },
	]

	// Loading state
	if (loading) {
		return (
			<div className="edit-project-page">
				<div className="edit-project-loading">
					<Loader2 className="spin" size={48} />
					<p>Loading project details...</p>
				</div>
			</div>
		)
	}

	// Error state
	if (error && !project) {
		return (
			<div className="edit-project-page">
				<div className="edit-project-error">
					<AlertCircle size={48} />
					<h3>Error Loading Project</h3>
					<p>{error}</p>
					<Button variant="outline" onClick={() => navigate('/projects')}>
						<ArrowLeft size={18} />
						Back to Projects
					</Button>
				</div>
			</div>
		)
	}

	// Not found state
	if (!project) {
		return (
			<div className="edit-project-page">
				<div className="edit-project-error">
					<FolderOpen size={48} />
					<h3>Project Not Found</h3>
					<p>The project you're looking for doesn't exist or has been deleted.</p>
					<Button variant="outline" onClick={() => navigate('/projects')}>
						<ArrowLeft size={18} />
						Back to Projects
					</Button>
				</div>
			</div>
		)
	}

	return (
		<div className="edit-project-page">
			{/* Header */}
			<div className="edit-project-header">
				<div className="edit-project-header__left">
					<button className="edit-project-header__back" onClick={handleBack}>
						<ArrowLeft size={16} />
						Back to Project
					</button>
					<div className="edit-project-header__title-row">
						<div className="edit-project-header__icon">
							<FolderOpen size={28} />
						</div>
						<div className="edit-project-header__info">
							<h1 className="edit-project-header__title">Edit Project</h1>
							<p className="edit-project-header__subtitle">{project.name}</p>
						</div>
					</div>
				</div>
				<div className="edit-project-header__actions">
					{hasChanges && <span className="edit-project-header__unsaved">Unsaved changes</span>}
					<Button variant="outline" onClick={handleBack} disabled={saving}>
						Cancel
					</Button>
					<Button variant="primary" onClick={handleSave} disabled={saving || !canEdit}>
						{saving ? (
							<>
								<Loader2 className="spin" size={16} />
								Saving...
							</>
						) : (
							<>
								<Save size={16} />
								Save Changes
							</>
						)}
					</Button>
				</div>
			</div>

			{/* Alerts */}
			{error && (
				<Alert
					variant="error"
					message={error}
					onClose={() => setError(null)}
					className="edit-project-alert"
				/>
			)}
			{successMessage && (
				<Alert
					variant="success"
					message={successMessage}
					onClose={() => setSuccessMessage(null)}
					className="edit-project-alert"
					autoDismiss={3000}
				/>
			)}

			{/* Section Navigation */}
			<div className="edit-project-nav">
				{sections.map((section) => {
					const Icon = section.icon
					return (
						<button
							key={section.id}
							className={`edit-project-nav__item ${activeSection === section.id ? 'active' : ''}`}
							onClick={() => setActiveSection(section.id)}
						>
							<Icon size={18} />
							{section.label}
						</button>
					)
				})}
			</div>

			{/* Content */}
			<div className="edit-project-content">
				{activeSection === 'basic' && (
					<BasicDetailsForm
						data={basicDetails}
						onChange={handleBasicDetailsChange}
						disabled={!canEdit}
						teamMembers={teamMembers}
					/>
				)}

				{activeSection === 'certificates' && (
					<CertificatesForm
						selectedCertificates={selectedCertificates}
						availableCertificates={availableCertificates}
						onChange={handleCertificatesChange}
						disabled={!canEdit}
					/>
				)}

				{activeSection === 'roles' && (
					<RolesForm value={selectedRoles} onChange={setSelectedRoles} disabled={!canEdit} />
				)}

				{activeSection === 'checklists' && (
					<ChecklistsForm
						items={checklistItems}
						certificates={availableCertificates.filter((c) => selectedCertificates.includes(parseInt(c.id, 10)))}
						teamMembers={teamMembers}
						onChange={handleChecklistsChange}
						disabled={!canEdit}
					/>
				)}
			</div>
		</div>
	)
}

export default EditProject
